import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Star, CheckCircle2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CSATSurvey() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [state, setState] = useState({ loading: true });
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/csat/${token}`);
        setState({ loading: false, ...data });
        if (data.submitted) {
          setDone(true);
          setRating(data.rating || 0);
          setComment(data.comment || "");
        } else {
          // Deep-link support from email: ?r=4 pre-selects
          const r = parseInt(params.get("r") || "0", 10);
          if (r >= 1 && r <= 5) setRating(r);
        }
      } catch (e) {
        setState({ loading: false, invalid: true });
        setError(e?.response?.data?.detail || "Survey link is invalid or expired.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async () => {
    if (!rating) { setError("Please pick a star rating."); return; }
    setSubmitting(true); setError("");
    try {
      await axios.post(`${API}/csat/${token}`, { rating, comment: comment || null });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not submit. Try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      {/* Header */}
      <div className="border-b border-slate-800 px-6 py-4 flex items-center gap-3">
        <img src="/kinos-logo.jpg" alt="KINOS" className="h-9 w-auto object-contain" data-testid="csat-brand-logo" />
        <div className="border-l border-slate-800 pl-3">
          <div className="font-display text-sm font-bold tracking-wide">KINOS</div>
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500 mt-0.5">IT Support · Feedback</div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-xl">
          {state.loading && (
            <div className="text-center text-slate-400 font-mono text-xs uppercase tracking-[0.3em]" data-testid="csat-loading">Loading survey…</div>
          )}

          {!state.loading && state.invalid && (
            <div className="border border-red-500/40 bg-red-500/10 p-8 rounded-sm text-center" data-testid="csat-invalid">
              <div className="font-display text-2xl font-bold text-red-400">Survey unavailable</div>
              <div className="text-sm text-slate-300 mt-3">{error}</div>
              <div className="text-xs text-slate-500 mt-6">If you think this is a mistake, contact <a className="text-cyan-400" href="mailto:service@kinos.tl">service@kinos.tl</a>.</div>
            </div>
          )}

          {!state.loading && !state.invalid && done && (
            <div className="border border-emerald-500/40 bg-emerald-500/10 p-10 rounded-sm text-center" data-testid="csat-thanks">
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-400" strokeWidth={1.5} />
              <div className="font-display text-3xl font-black mt-4">Thanks — we've got your feedback.</div>
              <div className="text-sm text-slate-400 mt-3">You rated your KINOS support experience:</div>
              <div className="flex items-center justify-center gap-1 mt-4">
                {[1,2,3,4,5].map((n) => (
                  <Star key={n} className={`h-7 w-7 ${n <= rating ? "text-amber-400 fill-amber-400" : "text-slate-700"}`} strokeWidth={1.5} />
                ))}
              </div>
              {comment && <div className="mt-5 text-sm text-slate-300 italic max-w-md mx-auto">&ldquo;{comment}&rdquo;</div>}
              <div className="text-xs text-slate-500 mt-8">You can safely close this tab.</div>
            </div>
          )}

          {!state.loading && !state.invalid && !done && (
            <div className="border border-slate-800 bg-[#121212] p-8 rounded-sm" data-testid="csat-form">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400">KINOS · Customer Feedback</div>
              <h1 className="font-display text-4xl font-black tracking-tight mt-3">How did we do?</h1>
              <p className="text-slate-400 mt-3 text-sm max-w-md">Your ticket has been resolved. It takes 5 seconds to rate the service you received.</p>

              {/* Stars */}
              <div className="mt-8 flex items-center gap-2 justify-center" onMouseLeave={() => setHover(0)}>
                {[1,2,3,4,5].map((n) => {
                  const active = n <= (hover || rating);
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      onMouseEnter={() => setHover(n)}
                      data-testid={`csat-star-${n}`}
                      className="p-2 group"
                      aria-label={`${n} star${n>1?'s':''}`}
                    >
                      <Star className={`h-12 w-12 transition-all group-hover:scale-110 ${active ? "text-amber-400 fill-amber-400" : "text-slate-700"}`} strokeWidth={1.5} />
                    </button>
                  );
                })}
              </div>
              <div className="text-center text-xs font-mono uppercase tracking-[0.3em] text-slate-500 mt-2" data-testid="csat-rating-label">
                {rating === 0 && "Tap a star to rate"}
                {rating === 1 && "Very poor"}
                {rating === 2 && "Poor"}
                {rating === 3 && "Okay"}
                {rating === 4 && "Good"}
                {rating === 5 && "Excellent"}
              </div>

              {/* Comment */}
              <div className="mt-8">
                <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Optional comment</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  placeholder="What could we improve? What worked well?"
                  className="mt-2 w-full bg-[#0A0A0A] border border-slate-800 rounded-sm p-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500"
                  data-testid="csat-comment"
                  maxLength={800}
                />
              </div>

              {error && <div className="mt-3 text-xs text-red-400" data-testid="csat-error">{error}</div>}

              <div className="mt-6 flex items-center justify-between gap-4">
                <div className="text-[11px] text-slate-500 flex items-center gap-2">
                  <ShieldCheck className="h-3 w-3" strokeWidth={1.5} /> One rating per ticket.
                </div>
                <button
                  onClick={submit}
                  disabled={submitting}
                  data-testid="csat-submit-btn"
                  className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-6 h-11 rounded-sm text-sm font-medium"
                >
                  {submitting ? "Sending…" : "Submit feedback"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-800 px-6 py-4 text-center text-[11px] text-slate-600 font-mono">
        KINOS · Rua Fatuhada, Dili, Timor-Leste · service@kinos.tl
      </div>
    </div>
  );
}
